import { Stack, Box } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { ButtonSubmit, FormAlert, FormInput, VerifyEmailAlert } from "components";
import { teamAppUrl, validateName, validateSubDomain } from "lib/utils";
import { useLoggedinUser, useTeam } from "lib/hooks";
import { teamClient } from "lib/clients";
import { useState } from "react";
import { object, SchemaOf } from "yup";

export enum EditTeamFields {
  name = 'name',
  identifier = 'identifier',
}

type State = {
  error: string;
  success: string;
}

type EditTeamSchema = {
  [EditTeamFields.name]: string;
  [EditTeamFields.identifier]: string;
}

const validationSchema: SchemaOf<EditTeamSchema> = object().shape({
  [EditTeamFields.name]: validateName.required('Team name is required'),
  [EditTeamFields.identifier]: validateSubDomain.required('Team subdomain is required'),
});

export const EditTeamForm = () => {
  const { data: team, mutate } = useTeam();
  const { isVerified, isLoading } = useLoggedinUser();
  const [ state, setState ] = useState<State>({
    error: "",
    success: "",
  });

  const onSubmit = async (values: EditTeamSchema): Promise<void> => {
    try {
      setState({ error: "", success: "" });

      const { data } = await teamClient.updateTeam(values);
      mutate(data, false);

      if (team.identifier !== data.identifier) {
        const teamUrl = teamAppUrl(data.identifier)
        window.location.href = teamUrl + '/account/team';
      }

      setState({ ...state, success: "Your team has been updated." });
    } catch (err) {
      setState({ ...state, error: "Could not update team, please try again." });
    }
  };

  const verificationRequired = !isLoading && !isVerified;

  return (
    <Formik
      initialValues={{
        name: team?.name,
        identifier: team?.identifier,
      }}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <Form onChange={() => setState({ error: "", success: "" })}>
        <Stack spacing={6} width="100%">
          {verificationRequired && (
            <VerifyEmailAlert content={"Verify your email to edit team settings."} />
          )}
          <FormAlert success={state.success} error={state.error} />
          <FormInput
            name={EditTeamFields.name}
            label={"Team name"}
            isRequired={true}
            type={"text"}
            spellCheck={false}
            isDisabled={!isVerified}
          />
          <FormInput
            label={"Custom team subdomain (team-url.packs.co)"}
            name={EditTeamFields.identifier}
            isRequired={true}
            type={"text"}
            isDisabled={!isVerified}
            spellCheck={false}
          />
          <Box>
            <ButtonSubmit>Update Team</ButtonSubmit>
          </Box>
        </Stack>
      </Form>
    </Formik>
  )
};
